import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  FileCheck, Shield, AlertTriangle, CheckCircle, XCircle, TrendingUp,
  ExternalLink, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const frameworkIcons = {
  'OWASP Top 10': Shield,
  'ISO 27001': FileCheck,
  'SOC 2': CheckCircle,
  'PCI-DSS': AlertTriangle,
};

const statusColors = {
  compliant: 'text-green-500 bg-green-500/10 border-green-500/20',
  at_risk: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
  non_compliant: 'text-red-500 bg-red-500/10 border-red-500/20',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }} className="text-xs">{e.name}: {e.value}</p>
      ))}
    </div>
  );
};

export default function CompliancePage() {
  const [frameworks, setFrameworks] = useState([]);
  const [posture, setPosture] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fwRes, posRes] = await Promise.all([
          axios.get(`${API}/compliance/frameworks`, { withCredentials: true }),
          axios.get(`${API}/compliance/posture`, { withCredentials: true }),
        ]);
        setFrameworks(fwRes.data);
        setPosture(posRes.data);
      } catch {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const chartData = posture.map(p => ({
    framework: p.framework,
    open: p.open_findings,
    resolved: p.resolved_findings,
    critical: p.critical_findings,
  }));

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="compliance-title">
          Compliance & GRC
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Framework mapping, compliance posture, and audit readiness</p>
      </div>

      {/* Compliance Posture Cards */}
      {posture.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {posture.map(p => {
            const Icon = frameworkIcons[p.framework] || FileCheck;
            return (
              <Card key={p.framework} className="metric-card" data-testid={`compliance-${p.framework}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-primary" />
                      <h3 className="font-medium text-sm">{p.framework}</h3>
                    </div>
                    <Badge variant="outline" className={`text-xs ${statusColors[p.status]}`}>
                      {p.status === 'compliant' ? 'Compliant' : p.status === 'at_risk' ? 'At Risk' : 'Non-Compliant'}
                    </Badge>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-end justify-between mb-1">
                      <span className="text-2xl font-bold font-heading">{p.compliance_score}%</span>
                      <span className="text-xs text-muted-foreground">{p.resolved_findings}/{p.total_findings} resolved</span>
                    </div>
                    <Progress value={p.compliance_score} className="h-2" />
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>{p.open_findings} open</span>
                    <span className="text-red-500">{p.critical_findings} critical</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {posture.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileCheck className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No compliance data yet. Run scans to generate findings.</p>
          </CardContent>
        </Card>
      )}

      {/* Compliance Chart */}
      {chartData.length > 0 && (
        <Card data-testid="compliance-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Findings by Framework</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="99%" debounce={80} height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="framework" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="resolved" name="Resolved" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="open" name="Open" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="critical" name="Critical" fill="#f97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Supported Frameworks */}
      <Card data-testid="frameworks-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading">Supported Frameworks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {frameworks.map(fw => (
              <div key={fw.id} className="p-4 rounded-xl border hover:border-primary/30 transition-colors" data-testid={`framework-${fw.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <h4 className="font-medium text-sm">{fw.name}</h4>
                </div>
                <p className="text-xs text-muted-foreground">{fw.description}</p>
                <p className="text-xs text-muted-foreground mt-1">{fw.categories} control categories</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
